import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Check, ExternalLink } from 'lucide-react';
import axios from 'axios';
import './FulfillmentList.css';

function FulfillmentList() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [nsAccountId, setNsAccountId] = useState('');
  const limit = 50;

  const fetchShipments = useCallback(async (pg = 1, q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit });
      if (q) params.set('search', q);
      const res = await axios.get(`/api/packing/recent?${params}`);
      setShipments(res.data.shipments || []);
      setTotal(res.data.total || 0);
      setPage(res.data.page || 1);
      if (res.data.nsAccountId) setNsAccountId(res.data.nsAccountId);
    } catch (err) {
      console.error('Failed to fetch shipments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShipments(1, '');
  }, [fetchShipments]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchShipments(1, search);
  };

  const handleClear = () => {
    setSearch('');
    setPage(1);
    fetchShipments(1, '');
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchShipments(newPage, search);
  };

  const [copiedId, setCopiedId] = useState(null);
  const totalPages = Math.ceil(total / limit);

  const handleCopyTracking = (id, tracking) => {
    navigator.clipboard.writeText(tracking);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const getTimeSince = (dateStr) => {
    if (!dateStr) return '-';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  const WHOLESALE_MARKUP = 1.7;
  const isWholesale = (cat) => cat != null;

  return (
    <div className="fl-page">
      <div className="fl-header">
        <h1 className="fl-title">Fulfillment List</h1>
        <span className="fl-count">{total} shipments</span>
      </div>

      <form onSubmit={handleSearch} className="fl-search">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order #, customer, or tracking..."
          className="fl-search-input"
        />
        <button type="submit" className="fl-btn">Search</button>
        {search && (
          <button type="button" className="fl-btn fl-btn-clear" onClick={handleClear}>Clear</button>
        )}
      </form>

      {loading ? (
        <div className="fl-loading">Loading...</div>
      ) : shipments.length === 0 ? (
        <div className="fl-empty">{search ? 'No shipments match your search' : 'No shipments found'}</div>
      ) : (
        <>
          <div className="fl-table-wrap">
            <table className="fl-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Tracking</th>
                  <th>Shipped</th>
                  <th>Cost</th>
                  <th>Billed</th>
                  <th>Status</th>
                  <th>NS</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map(s => (
                  <tr key={s.id} className={s.status === 'voided' ? 'fl-row-voided' : ''}>
                    <td className="fl-mono">{s.tran_id}</td>
                    <td>{s.customer_name}</td>
                    <td className="fl-tracking">
                      {(s.all_tracking_numbers || []).length > 0 ? (
                        <div className="fl-tracking-list">
                          {s.all_tracking_numbers.map((tn, i) => (
                            <span key={i} className="fl-tracking-wrap">
                              <span className="fl-mono">{tn}</span>
                              <button
                                className="fl-copy-btn"
                                onClick={() => handleCopyTracking(`${s.id}-${i}`, tn)}
                                title="Copy tracking number"
                              >
                                {copiedId === `${s.id}-${i}` ? <Check size={13} /> : <Copy size={13} />}
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : '-'}
                    </td>
                    <td title={formatDate(s.fulfilled_at)}>{getTimeSince(s.fulfilled_at)} ago</td>
                    <td>
                      {s.shipping_cost ? (
                        <div className="fl-cost-cell">
                          <span className="fl-cost">${parseFloat(s.shipping_cost).toFixed(2)}</span>
                          {s.retail_shipping_cost && parseFloat(s.retail_shipping_cost) > parseFloat(s.shipping_cost) && (
                            <>
                              <span className="fl-retail-cost">${parseFloat(s.retail_shipping_cost).toFixed(2)}</span>
                              <span className="fl-savings">
                                -{Math.round((1 - parseFloat(s.shipping_cost) / parseFloat(s.retail_shipping_cost)) * 100)}%
                              </span>
                            </>
                          )}
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      {s.shipping_cost && isWholesale(s.customer_category) ? (
                        <span className="fl-billed">${(parseFloat(s.shipping_cost) * WHOLESALE_MARKUP).toFixed(2)}</span>
                      ) : '-'}
                    </td>
                    <td>
                      <span className={`fl-badge fl-badge-${s.status}`}>{s.status}</span>
                    </td>
                    <td>
                      {s.netsuite_fulfillment_id ? (
                        <a
                          href={`https://3646798.app.netsuite.com/app/accounting/transactions/itemfulfillment.nl?id=${s.netsuite_fulfillment_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="fl-ns-link"
                          title="View in NetSuite"
                        >
                          <ExternalLink size={14} />
                        </a>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="fl-pagination">
              <button
                className="fl-btn fl-btn-sm"
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
              >
                Prev
              </button>
              <span className="fl-page-info">Page {page} of {totalPages}</span>
              <button
                className="fl-btn fl-btn-sm"
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default FulfillmentList;
